import React, { useState, useEffect, useRef } from "react";
import user from "../../store/user";
import updateSeekerEmailNotifSettings from "../../services/updateSeekerEmailNotifSettings";
import fetchSeekerEmailNotifSettings from "../../services/fetchSeekerEmailNotifSettings";


import "./seekerEmailNotificationSettings.scss";
import { Header, Modal } from "semantic-ui-react";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import Heading from "../../components/Heading";
import { toast, ToastContainer } from "react-toastify";
import InputBox from "../../components/InputBox";
import PillButton from "../../components/PillButton";
import ActionIcons from "../../components/ActionIcons";
import { getQueryParams } from "../../utils/utilFunctions";

const EMAIL_SETTINGS = [
    { key: 'jobFeed', label: 'Daily New Jobs', tooltip: 'New job matches - daily jobfeeds' },
    { key: 'boost', label: 'Jobs Relevant to Me', tooltip: 'Recommended jobs - interesting relevant jobs for your profile.' },
    { key: 'jobAction', label: "Recruiter's Actions", tooltip: 'Actions on your application - recruiter action email in your inbox.' },
    { key: 'promotions', label: 'Promotional', tooltip: 'General updates - good job offers or other promotional content from recruiters.' },
    { key: 'education', label: 'Educational - Learn and Grow', tooltip: 'Learning & courses notifications based on your profile.' },
    { key: 'jobApply', label: 'Applied Jobs', tooltip: 'Application confirmation emails right after applying to any job.' },
    { key: 'followUp', label: 'Follow-up', tooltip: 'Follow-up activity - follow-ups credited and used in your account.' },
    { key: 'testPending', label: 'Pending Test', tooltip: 'Pending test notification email.' },
    { key: 'chat', label: 'Chat Notifications', tooltip: 'Recruiter messages - chat notifications in your inbox.' },
    { key: 'search', label: 'Recruiters Viewing My Profile', tooltip: 'Profile views by recruiters in database.' },
    { key: 'unpublish', label: 'Unpublished Job Notification', tooltip: 'Updates about the job unpublished status.' }
];

const PN_SETTINGS = [
    { key: 'jobFeed', label: 'Daily New Jobs', tooltip: 'New job matches - daily jobfeeds' },
    { key: 'boost', label: 'Jobs Relevant to Me', tooltip: 'Recommended jobs - interesting relevant jobs for your profile.' },
    { key: 'jobAction', label: "Recruiter's Actions", tooltip: 'Actions on your application - recruiter push notifications.' },
    { key: 'promotions', label: 'Promotional', tooltip: 'General updates - promotional push notifications.' },
    { key: 'education', label: 'Educational - Learn and Grow', tooltip: 'Learning & courses notifications based on your profile.' },
    { key: 'chat', label: 'Chat Notifications', tooltip: 'Recruiter messages - chat push notifications.' },
    { key: 'search', label: 'Recruiters Viewing My Profile', tooltip: 'Profile views by recruiters in database.' },
    { key: 'unpublish', label: 'Unpublished Job Notification', tooltip: 'Updates about the job unpublished status.' }
];

const ACCOUNT_SETTINGS = [
    { key: 'hideResume', label: 'Hide Resume' },
    { key: 'invalidEmail', label: 'Invalid Email' },
    { key: 'coverLetter', label: 'Cover Letter' },
    { key: 'hideStories', label: 'Hide Stories' },
    { key: 'similarCourseEnquiry', label: 'Similar Course Enquiry' },
    { key: 'twoFactorAuth', label: 'Two Factor Auth' }
];

const buildGroupObj = (defs, value) => {
    const obj = {};
    defs.forEach(def => {
        obj[def.key] = value;
    });
    return obj;
};

const isAllSelected = (defs, group) => {
    return defs.every(def => group && group[def.key] == 1);
};

const SettingsGroupCard = ({ title, icon, variant, defs, group, onToggle, showSelectAll = true }) => {

    if (!group || !defs.length) {
        return null;
    }

    const allSelected = isAllSelected(defs, group);

    return (
        <div className={`settings-group-card ${variant}`}>
            <div className="settings-group-header">
                <h4><span className="group-icon">{icon}</span>{title}</h4>
                {showSelectAll ? (
                    <label className="select-all">
                        <InputBox type='checkbox' name='selectAll' value={allSelected ? 1 : 0} onChange={() => onToggle(null)} checked={allSelected} />
                        <span>Select All</span>
                    </label>
                ) : null}
            </div>
            <div className="settings-group-body">
                {defs.map(def => (
                    <div className="option-row" key={def.key}>
                        <label className="option-main">
                            <InputBox type='checkbox' name={def.key} value={group[def.key]} onChange={() => onToggle(def.key)} checked={group[def.key] ? true : false} />
                            <span className="option-label">{def.label}</span>
                        </label>
                        {def.tooltip ? <ActionIcons type="onHoldJob" tooltipMessage={def.tooltip} /> : null}
                    </div>
                ))}
            </div>
        </div>
    );
};

const SettingsEmailNotificationSettings = (props) => {

    const [admin, setAdmin] = useState({
        adminId: null,
    })
    const [fetchApiCall, setFetchApiCall] = useState(false)
    const [searched, setSearched] = useState(false)

    const [seekers, setSeekers] = useState([])

    // synchronous mirror of seekers: quick successive toggles must read the
    // latest values before React commits the previous state update
    const seekersRef = useRef([])
    // serializes update POSTs so an earlier payload can never overwrite a later one
    const updateQueueRef = useRef(Promise.resolve())

    const applySeekers = (updater) => {
        const next = typeof updater === 'function' ? updater(seekersRef.current) : updater;
        seekersRef.current = next;
        setSeekers(next);
    }

    const [allEmails, setAllEmails] = useState([])
    const [selectedEmails, setSelectedEmails] = useState([])

    const [allModalEmails, setAllModalEmails] = useState([])
    const [selectedModalEmails, setSelectedModalEmails] = useState([])

    const [openModalStatus, setOpenModalStatus] = useState(false)

    const [modalEmailSettings, setModalEmailSettings] = useState(buildGroupObj(EMAIL_SETTINGS, 1))
    const [modalPnSettings, setModalPnSettings] = useState(buildGroupObj(PN_SETTINGS, 1))

    useEffect(() => {
        const queryObj = getQueryParams(props.location.search);
        const fetchProfile = async () => {
            return await user.getProfile()
        }
        fetchProfile().then(userData => {
            if (userData.adminType == 2 || userData.adminType == 3 || userData.adminType == 4) {
                setAdmin({
                    adminId: userData.id,
                })

                const email = queryObj.email;

                if (email) {
                    setAllEmails([{ key: 0, value: email, text: email }])
                    setSelectedEmails([{ email, id: 0 }])
                    getSeekerNotificationSettings(userData.id, email);
                }

            } else {
                window.location.href = process.env.REACT_APP_LOGIN_URL;
            }
        });

    }, [])

    const getSeekerNotificationSettings = async (adminId, seekerEmail) => {
        setFetchApiCall(true);
        try {
            const res = await fetchSeekerEmailNotifSettings(adminId, { seekerEmail })
            const { data, invalidEmails } = res.data.data;

            if (invalidEmails && invalidEmails.length) {
                toast.error(`${invalidEmails.join(', ')} ${invalidEmails.length > 1 ? 'are' : 'is'} invalid`)
            }

            applySeekers(data)
            setSearched(true)
            setFetchApiCall(false)
        }
        catch (err) {
            console.log(err);
            setFetchApiCall(false)
        }
    }


    const searchSeeker = async () => {

        if (!selectedEmails.length) {
            return toast.error('Enter at least one seeker email')
        }

        const emails = selectedEmails.map(email => email.email).join(',')
        await getSeekerNotificationSettings(admin.adminId, emails)

    };

    const onSettingToggle = async (index, groupName, defs, key) => {

        const seeker = seekersRef.current[index];
        const currentGroup = seeker[groupName];

        let updatedGroup;

        if (key) {
            updatedGroup = {};
            defs.forEach(def => {
                if (currentGroup[def.key] !== undefined) {
                    updatedGroup[def.key] = currentGroup[def.key];
                }
            });
            updatedGroup[key] = currentGroup[key] ? 0 : 1;
        } else {
            updatedGroup = buildGroupObj(defs, isAllSelected(defs, currentGroup) ? 0 : 1);
        }

        // apply optimistically so the next toggle builds on these values
        applySeekers(prev => prev.map((item, i) => i === index ? { ...item, [groupName]: updatedGroup } : item))

        const queuedPost = updateQueueRef.current.then(() => updateSeekerEmailNotifSettings(admin.adminId, {
            seekerEmail: seeker.email,
            [groupName]: updatedGroup
        }));
        updateQueueRef.current = queuedPost.catch(() => { });

        try {
            await queuedPost;
            toast.success('Settings updated successfully')

        } catch (e) {
            console.log(e);
            applySeekers(prev => prev.map((item, i) => i === index ? { ...item, [groupName]: currentGroup } : item))
            toast.error('Something went wrong')
        }
    }

    const handlePillChange = (ev, data) => {
        const allEmail = [];
        const selectedEmail = [];

        data.value.forEach((email, i) => {
            allEmail.push({ key: i, value: email, text: email })
            selectedEmail.push({ email, id: i })
        })

        setAllEmails(allEmail)
        setSelectedEmails(selectedEmail)
    }

    const handleModalPillChange = (ev, data) => {
        const allEmail = [];
        const selectedEmail = [];

        data.value.forEach((email, i) => {
            allEmail.push({ key: i, value: email, text: email })
            selectedEmail.push({ email, id: i })
        })

        setAllModalEmails(allEmail)
        setSelectedModalEmails(selectedEmail)
    }

    const handleModalClose = () => {
        setOpenModalStatus(false)
        setModalEmailSettings(buildGroupObj(EMAIL_SETTINGS, 1))
        setModalPnSettings(buildGroupObj(PN_SETTINGS, 1))
        setAllModalEmails([])
        setSelectedModalEmails([])
    }

    const onModalEmailToggle = (key) => {
        if (key) {
            setModalEmailSettings(prev => ({ ...prev, [key]: prev[key] ? 0 : 1 }))
        } else {
            setModalEmailSettings(prev => buildGroupObj(EMAIL_SETTINGS, isAllSelected(EMAIL_SETTINGS, prev) ? 0 : 1))
        }
    }

    const onModalPnToggle = (key) => {
        if (key) {
            setModalPnSettings(prev => ({ ...prev, [key]: prev[key] ? 0 : 1 }))
        } else {
            setModalPnSettings(prev => buildGroupObj(PN_SETTINGS, isAllSelected(PN_SETTINGS, prev) ? 0 : 1))
        }
    }

    const onClickModalActionBtn = async () => {

        const emails = selectedModalEmails.map(email => email.email)

        try {
            const res = await updateSeekerEmailNotifSettings(admin.adminId, {
                seekerEmail: emails,
                emailSettings: modalEmailSettings,
                pnSettings: modalPnSettings
            })

            if (res.data.data.length) {
                const invalidEmails = res.data.data.join(', ');
                return toast.error(`${invalidEmails} ${res.data.data.length > 1 ? 'are' : 'is'} invalid`)
            }

            toast.success("Settings successfully updated")
            handleModalClose();

            if (selectedEmails.length) {
                await searchSeeker();
            }

        } catch (e) {
            console.log(e);
            toast.error('Something went wrong')
        }
    }

    return (
        <div className="recruiter-company-list-wrapper seeker-notification-page">
            <ToastContainer
                position="bottom-left"
                autoClose={5000}
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnVisibilityChange
                draggable={false}
                pauseOnHover
            />

            <div className="company-list-heading">
                <Heading text="Seeker Notification Settings" />
            </div>


            <div className='seeker-settings-toolbar'>
                <div className="toolbar-search">
                    <label>Search Seeker Email</label>
                    <div className="toolbar-search-row">
                        <PillButton data={allEmails} placeholder='Enter seeker email' name='selectedEmails' onChange={handlePillChange} allowAdditions onAddOtherLocations={handlePillChange} defaultValue={[]} maximumValues={10} />

                        <Button skin="dark" className="btn-themed btn-search" text="Search" clickButton={searchSeeker} />
                    </div>
                </div>
                <Button skin="dark" className="btn-themed btn-bulk-edit" text="&#9998; Edit For Multiple Seekers" clickButton={() => { setOpenModalStatus(true) }} />
            </div>

            <div className="row text-center">

                <Modal className="seeker-notif-setting-modal" size="large" open={openModalStatus} onClose={handleModalClose}>
                    <Header content='Edit Notification Settings' />
                    <Modal.Content>
                        <PillButton data={allModalEmails} placeholder='Enter seeker email' name='selectedModalEmails' onChange={handleModalPillChange} allowAdditions onAddOtherLocations={handleModalPillChange} defaultValue={[]} maximumValues={10} />

                        <div className="settings-groups modal-groups">
                            <SettingsGroupCard
                                title="Email Notifications"
                                icon={<>&#9993;</>}
                                variant="email"
                                defs={EMAIL_SETTINGS}
                                group={modalEmailSettings}
                                onToggle={onModalEmailToggle}
                            />
                            <SettingsGroupCard
                                title="Push Notifications"
                                icon={<>&#128276;</>}
                                variant="push"
                                defs={PN_SETTINGS}
                                group={modalPnSettings}
                                onToggle={onModalPnToggle}
                            />
                        </div>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button disabled={selectedModalEmails.length == 0} clickButton={onClickModalActionBtn} skin='dark' className="btn-themed btn-confirm" text='Confirm' />
                        <Button clickButton={handleModalClose} skin="light" className="btn-modal-close" text="Close" />

                    </Modal.Actions>
                </Modal>


                {fetchApiCall ? (
                    <Loader />
                ) : (
                    seekers.length ? (
                        <div className="content">
                            {seekers.map((seeker, index) => (
                                <div className="seeker-settings-card" key={seeker.id}>
                                    <div className="seeker-settings-card-header">
                                        <span className="seeker-avatar">{(seeker.name || seeker.email || '?').charAt(0).toUpperCase()}</span>
                                        <div className="seeker-identity">
                                            <span className="seeker-name">{seeker.name || '—'}</span>
                                            <span className="seeker-email">{seeker.email}</span>
                                        </div>
                                        <span className="seeker-id-badge">ID: {seeker.id}</span>
                                    </div>

                                    {seeker.fetchFailed || !seeker.emailSettings ? (
                                        <div className="seeker-settings-error">
                                            Could not fetch settings for this seeker
                                        </div>
                                    ) : (
                                        <div className="settings-groups">
                                            <SettingsGroupCard
                                                title="Email Notifications"
                                                icon={<>&#9993;</>}
                                                variant="email"
                                                defs={EMAIL_SETTINGS}
                                                group={seeker.emailSettings}
                                                onToggle={(key) => onSettingToggle(index, 'emailSettings', EMAIL_SETTINGS, key)}
                                            />
                                            <SettingsGroupCard
                                                title="Push Notifications"
                                                icon={<>&#128276;</>}
                                                variant="push"
                                                defs={PN_SETTINGS}
                                                group={seeker.pnSettings}
                                                onToggle={(key) => onSettingToggle(index, 'pnSettings', PN_SETTINGS, key)}
                                            />
                                            <SettingsGroupCard
                                                title="Account Settings"
                                                icon={<>&#128100;</>}
                                                variant="account"
                                                defs={ACCOUNT_SETTINGS.filter(def => seeker.accountSettings && seeker.accountSettings[def.key] !== undefined)}
                                                group={seeker.accountSettings}
                                                onToggle={(key) => onSettingToggle(index, 'accountSettings', ACCOUNT_SETTINGS, key)}
                                                showSelectAll={false}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="seeker-settings-empty">
                            <span className="empty-icon">{searched ? <>&#128269;</> : <>&#9993;</>}</span>
                            <p>{searched ? 'No records found for the searched email(s)' : 'Search seeker email(s) to view their notification settings'}</p>
                        </div>
                    )
                )
                }
            </div>
        </div>
    );
};

export default SettingsEmailNotificationSettings;
